import type { useI18n } from 'vue-i18n';
import type { AuthProviderDefinition, ProviderIcon } from '../../types/auth-providers/define';
import { translateString } from '#layers/i18n';
import { prependIdentityFields } from './identity';
import { auth0 } from './presets/auth0';
import { github } from './presets/github';
import { gitlab } from './presets/gitlab';
import { google } from './presets/google';
import { microsoftEntra } from './presets/microsoft-entra';
import { okta } from './presets/okta';
import { oauth2 } from './protocols/oauth2';
import { password } from './protocols/password';
import { resolveProviderVisuals } from './resolve-provider-visuals';

export { AuthProtocol, defineAuthProvider } from '../../types/auth-providers/define';
export type { AuthProviderDefinition, AuthProviderFormSchemaContext, AuthProviderWalkthroughStep, ProviderIcon } from '../../types/auth-providers/define';

export { resolveAuthProvider } from './resolve';

export type { ResolvedAuthProviderDefinition } from './resolve';
export { FALLBACK_PROVIDER_ICON } from './resolve-provider-icon';
export { buildCreateBody, buildUpdateBody, WireAuthProviderListEntrySchema, WireAuthProviderListResponseSchema } from './wire';
export type {
	WireAuthProviderAdminRecord,
	WireAuthProviderCreateBody,
	WireAuthProviderListEntry,
	WireAuthProviderUpdateBody,
} from './wire';

/**
 * Auth provider registry. Mirror of the data connector pattern in
 * `connectors/`.
 *
 * `prependIdentityFields` injects the shared identity group (apiName) on each
 * definition with a form (base or preset). Preset `extends` references stay
 * pointed at the unwrapped base, so `context.base.resolvedOptions` doesn't
 * carry identity into the preset's options function — no double-injection.
 */
export const authProviders = {
	'oauth2': prependIdentityFields(oauth2),
	password,
	'google': prependIdentityFields(google),
	'github': prependIdentityFields(github),
	'microsoft-entra': prependIdentityFields(microsoftEntra),
	'okta': prependIdentityFields(okta),
	'gitlab': prependIdentityFields(gitlab),
	'auth0': prependIdentityFields(auth0),
} as const;

export const authProvidersById: Record<string, AuthProviderDefinition> =
	Object.fromEntries(
		Object.values(authProviders).map((p): [string, AuthProviderDefinition] => [p.id, p as AuthProviderDefinition]),
	);

export function getAuthProviderDefinition(id: string): AuthProviderDefinition | undefined {
	return authProvidersById[id];
}

/**
 * Resolve a list-response entry to its registry definition. Tries `preset`
 * first, then `protocol`. Callers handle the `undefined` fallback themselves.
 */
export function getAuthProviderForResponse(
	response: { preset?: string | null; protocol: string },
): AuthProviderDefinition | undefined {
	return (response.preset ? authProvidersById[response.preset] : undefined)
		?? authProvidersById[response.protocol];
}

/**
 * Human-readable name for a provider record. Precedence:
 * admin `displayName` → preset brand name → `apiName`.
 *
 * `i18n` must be passed by the caller — captured during setup. `translateString`
 * has no default and cannot call `useI18n()` itself from a post-setup callback.
 */
export function getDisplayName(
	record: { displayName?: string | null; preset?: string | null; protocol: string; apiName: string },
	i18n: ReturnType<typeof useI18n>,
): string {
	if (record.displayName) return translateString(record.displayName, i18n);

	// Preset providers have a brand name; custom providers fall through to
	// `apiName` because their generic protocol name ("Custom OAuth 2") would
	// be unhelpful as a button label.
	if (record.preset) {
		const def = getAuthProviderForResponse(record);
		if (def) return translateString(def.name, i18n);
	}

	return record.apiName;
}

/**
 * Resolve a provider's display name and visuals in one pass for the rare caller
 * that needs both (e.g. the login method button). Composes `getDisplayName` and
 * `resolveProviderVisuals` so the provider record is read once instead of twice.
 */
export function resolveProviderDisplay(
	record: {
		displayName?: string | null;
		preset?: string | null;
		protocol: string;
		apiName: string;
		icon?: string | null;
		iconColor?: string | null;
	},
	def: { icon?: string | ProviderIcon; iconColor?: string } | undefined,
	isDark: boolean,
	i18n: ReturnType<typeof useI18n>,
): { displayName: string; iconName: string; iconColor: string | undefined } {
	return {
		displayName: getDisplayName(record, i18n),
		...resolveProviderVisuals(record, def, isDark),
	};
}
